import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  ResponsiveList,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useMediaQuery,
  useToast,
  todayInArgentina,
} from '@catering/ui';
import type { ActionBarAction, StatusBadgeProps } from '@catering/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { FilePlus2, Receipt, Search } from 'lucide-react';
import { useState, type JSX } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import { hasPermission } from '../../auth/has-permission';
import { searchAllCustomers } from '../../customers/api';
import { formatPriceCents } from '../../contracts/format-price';
import { getFiscalModeBannerText } from '../../invoices/fiscal-mode-banner';
import { formatInvoiceNumber } from '../../invoices/format-invoice-number';
import {
  INVOICE_STATUSES,
  INVOICE_TYPES,
  generateBillingDraft,
  getFiscalMode,
  issueInvoicesBatch,
  listInvoices,
  type BatchIssuanceOutcome,
  type Invoice,
  type InvoiceStatus,
  type InvoiceType,
} from '../../invoices/api';
import { mapInvoiceError, skippedOperatingUnitMessage } from '../../invoices/invoice-api-errors';
import { OperatingUnitSelect } from '../../components/operating-unit-select';
import { useOperatingUnitOptions } from '../../operating-units/use-operating-unit-options';
import { ListLoadError } from '../../components/list-load-error';
import { GuideLink } from '../../guide/guide-link';
import { formatCalendarDate } from '../../orders/format-calendar-date';

function firstDayOfMonthIso(): string {
  return `${todayInArgentina().slice(0, 7)}-01`;
}

function todayIso(): string {
  return todayInArgentina();
}

const invoicesRoute = getRouteApi('/protected/invoices');

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Borrador',
  PENDING_ARCA: 'Enviada a ARCA',
  ISSUED: 'Emitida',
  REJECTED: 'Rechazada',
  PARTIALLY_PAID: 'Pago parcial',
  PAID: 'Pagada',
  OVERDUE: 'Vencida',
};

/** Only issued invoices carry a collectible balance; drafts and rejected ones never do. */
const ISSUED_INVOICE_STATUSES: ReadonlySet<InvoiceStatus> = new Set([
  'ISSUED',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
]);

function renderSaldo(invoice: Invoice): string {
  return ISSUED_INVOICE_STATUSES.has(invoice.status)
    ? formatPriceCents(invoice.pendingBalanceCents)
    : '—';
}

const TYPE_LABELS: Record<InvoiceType, string> = {
  FACTURA_A: 'Factura A',
  FACTURA_B: 'Factura B',
  FACTURA_C: 'Factura C',
};

/** The `invoice` `StatusBadge` domain has no "skipped" status, so that outcome is a plain label. */
const BATCH_OUTCOME_BADGE: Record<'ISSUED' | 'REJECTED', StatusBadgeProps> = {
  ISSUED: { domain: 'invoice', status: 'ISSUED' },
  REJECTED: { domain: 'invoice', status: 'REJECTED' },
};

interface FilterPatch {
  status?: InvoiceStatus | undefined;
  customerId?: string | undefined;
  periodStart?: string | undefined;
  periodEnd?: string | undefined;
  invoiceType?: InvoiceType | undefined;
}

/**
 * `invoice-drafts/spec.md` → "Listado de facturas con filtros" +
 * "Pantallas de generación y revisión en el back-office". "Generar
 * borradores" is a `Modal` on this same list, same pattern
 * `DeliveryNoteListPage`'s own "Generar remitos" already established —
 * not the separate `/invoices/generate` route `design.md` §9.1/§9.3
 * originally planned. `invoiceType` filters client-side over the current
 * page: `GET /invoices` (`listInvoicesQuerySchema`) has no `invoiceType`
 * param, and adding one is outside this PR's `apps/web-admin`-only scope.
 */
export function InvoiceListPage(): JSX.Element {
  const search = invoicesRoute.useSearch();
  const navigate = invoicesRoute.useNavigate();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isDesktop = useMediaQuery('(min-width: 640px)');

  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [draftPeriodStart, setDraftPeriodStart] = useState(firstDayOfMonthIso());
  const [draftPeriodEnd, setDraftPeriodEnd] = useState(todayIso());

  const [draftRangeError, setDraftRangeError] = useState(false);

  const generateMutation = useMutation({
    mutationFn: () => generateBillingDraft(draftPeriodStart, draftPeriodEnd),
    onSuccess: async ({ invoices, skippedOperatingUnits }) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // Skipped units are never silent: the modal stays open with the result and the way to fix it.
      if (skippedOperatingUnits.length === 0) {
        setGenerateModalOpen(false);
        toast(
          'success',
          invoices.length === 0
            ? 'No había remitos para facturar en ese período'
            : `Se generaron ${invoices.length} borradores`,
        );
      }
    },
  });

  function confirmGenerate(): void {
    if (draftPeriodStart > draftPeriodEnd) {
      setDraftRangeError(true);
      return;
    }

    setDraftRangeError(false);
    generateMutation.mutate();
  }

  function openGenerateModal(): void {
    setDraftRangeError(false);
    generateMutation.reset();
    setGenerateModalOpen(true);
  }

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  // Invoices picked when the modal opened: the list may refetch (and drop them) before the results show.
  const [batchInvoices, setBatchInvoices] = useState<Invoice[]>([]);

  function openBatchModal(): void {
    setBatchInvoices(allItems.filter((invoice) => selectedIds.has(invoice.id)));
    setBatchModalOpen(true);
  }

  function batchLabel(result: BatchIssuanceOutcome): string {
    const invoice = batchInvoices.find((candidate) => candidate.id === result.invoiceId);

    if (invoice === undefined) {
      return 'Factura';
    }

    return result.number === undefined
      ? invoice.customerLegalNameSnapshot
      : formatInvoiceNumber(result.pointOfSale ?? invoice.pointOfSale, invoice.invoiceType, result.number);
  }

  const batchMutation = useMutation({
    mutationFn: () => issueInvoicesBatch([...selectedIds]),
    onSuccess: async () => {
      setSelectedIds(new Set());
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  function toggleSelected(invoiceId: string): void {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (next.has(invoiceId)) {
        next.delete(invoiceId);
      } else {
        next.add(invoiceId);
      }

      return next;
    });
  }

  function closeBatchModal(): void {
    setBatchModalOpen(false);
    batchMutation.reset();
  }

  // Not URL-owned: the unit filter and column only exist with more than one visible unit.
  const [operatingUnitId, setOperatingUnitId] = useState('');
  const showUnits = useOperatingUnitOptions().length > 1;

  const query = useQuery({
    queryKey: [
      'invoices',
      search.status,
      search.customerId,
      operatingUnitId,
      search.periodStart,
      search.periodEnd,
    ],
    queryFn: () =>
      listInvoices({
        status: search.status,
        customerId: search.customerId,
        operatingUnitId: operatingUnitId === '' ? undefined : operatingUnitId,
        periodStart: search.periodStart,
        periodEnd: search.periodEnd,
        page: 1,
        pageSize: 100,
      }),
    placeholderData: keepPreviousData,
  });

  const fiscalModeQuery = useQuery({
    queryKey: ['invoices', 'fiscal-mode'],
    queryFn: getFiscalMode,
  });

  const customersQuery = useQuery({
    queryKey: ['customers-for-invoices'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  const customers = customersQuery.data?.items ?? [];
  const canCreate = hasPermission(currentUser.data, 'invoice.create');
  const canIssue = hasPermission(currentUser.data, 'invoice.issue');

  function applyFilters(patch: FilterPatch): void {
    setSelectedIds(new Set());
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  const allItems = query.data?.items ?? [];
  const items =
    search.invoiceType === undefined
      ? allItems
      : allItems.filter((invoice) => invoice.invoiceType === search.invoiceType);

  const hasActiveFilters =
    search.status !== undefined ||
    search.customerId !== undefined ||
    search.periodStart !== undefined ||
    search.periodEnd !== undefined ||
    search.invoiceType !== undefined;

  const showTestBanner =
    fiscalModeQuery.data?.isTestMode === true || items.some((invoice) => invoice.isTestInvoice);

  const headerActions: ActionBarAction[] = [
    ...(canCreate
      ? [{ label: 'Generar borradores', icon: FilePlus2, onClick: openGenerateModal }]
      : []),
    ...(canIssue
      ? [
          {
            label: 'Emitir seleccionadas',
            onClick: openBatchModal,
            disabled: selectedIds.size === 0,
          },
        ]
      : []),
  ];
  const primaryAction = headerActions[0];
  const secondaryActions = headerActions.slice(1);

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Receipt}
        title="Facturación"
        description="Consultá las facturas generadas y su estado de emisión ante ARCA."
        actions={
          isDesktop ? (
            <div className="flex items-center gap-2">
              {canCreate ? (
                <Button icon={FilePlus2} onClick={openGenerateModal}>
                  Generar borradores
                </Button>
              ) : null}
              {canIssue ? (
                <Button
                  variant="secondary"
                  disabled={selectedIds.size === 0}
                  onClick={openBatchModal}
                >
                  Emitir seleccionadas
                </Button>
              ) : null}
              <GuideLink moduleId="billing" />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {primaryAction === undefined ? null : (
                <ActionBar primary={primaryAction} secondary={secondaryActions} />
              )}
              <GuideLink moduleId="billing" />
            </div>
          )
        }
      />

      {showTestBanner ? (
        <Alert tone="warning" title={getFiscalModeBannerText(fiscalModeQuery.data?.environment)}>
          El sistema todavía está en modo de prueba, sin conexión real con ARCA. El CAE (el código de autorización que ARCA da a cada factura) es simulado.
        </Alert>
      ) : null}

      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Período desde</span>
            <input
              type="date"
              value={search.periodStart ?? ''}
              onChange={(event) =>
                applyFilters({
                  periodStart: event.target.value === '' ? undefined : event.target.value,
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Período hasta</span>
            <input
              type="date"
              value={search.periodEnd ?? ''}
              onChange={(event) =>
                applyFilters({
                  periodEnd: event.target.value === '' ? undefined : event.target.value,
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Empresa</span>
            <select
              value={search.customerId ?? ''}
              onChange={(event) =>
                applyFilters({
                  customerId: event.target.value === '' ? undefined : event.target.value,
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todas</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.legalName}
                </option>
              ))}
            </select>
          </label>

          <OperatingUnitSelect
            id="invoice-operating-unit"
            label="Punto de venta"
            allLabel="Todos los puntos"
            value={operatingUnitId}
            onChange={(value) => {
              setSelectedIds(new Set());
              setOperatingUnitId(value);
            }}
          />

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Estado</span>
            <select
              value={search.status ?? ''}
              onChange={(event) =>
                applyFilters({
                  status:
                    event.target.value === '' ? undefined : (event.target.value as InvoiceStatus),
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos</option>
              {INVOICE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Tipo</span>
            <select
              value={search.invoiceType ?? ''}
              onChange={(event) =>
                applyFilters({
                  invoiceType:
                    event.target.value === '' ? undefined : (event.target.value as InvoiceType),
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos</option>
              {INVOICE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      {query.isError ? (
        <ListLoadError
          title="No pudimos cargar las facturas"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton data-testid="invoices-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="invoices-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="invoices-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="invoices-skeleton-row" className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && items.length === 0 ? (
        <EmptyState
          icon={hasActiveFilters ? Search : Receipt}
          title={
            hasActiveFilters ? 'No se encontraron facturas.' : 'Todavía no hay facturas generadas.'
          }
        />
      ) : null}

      {query.data !== undefined && query.data.total > allItems.length ? (
        <Alert tone="warning">
          Mostrando {allItems.length} de {query.data.total} facturas. Ajustá los filtros para ver el
          resto.
        </Alert>
      ) : null}

      {query.data !== undefined && items.length > 0 ? (
        <ResponsiveList
          items={items}
          rowKey={(invoice) => invoice.id}
          caption="Facturas"
          getRowTestId={(invoice) => `invoice-card-${invoice.id}`}
          emptyState={null}
          {...(canIssue
            ? {
                renderActions: (invoice: Invoice) =>
                  invoice.status === 'DRAFT' ? (
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar factura ${formatInvoiceNumber(invoice.pointOfSale, invoice.invoiceType, invoice.number)}`}
                      checked={selectedIds.has(invoice.id)}
                      onChange={() => toggleSelected(invoice.id)}
                    />
                  ) : null,
              }
            : {})}
          columns={[
            {
              key: 'number',
              header: 'Número',
              render: (invoice) => (
                <Link
                  to="/invoices/$id"
                  params={{ id: invoice.id }}
                  className="font-medium text-slate-900 hover:underline"
                >
                  {formatInvoiceNumber(invoice.pointOfSale, invoice.invoiceType, invoice.number)}
                </Link>
              ),
            },
            { key: 'customer', header: 'Cliente', render: (invoice) => invoice.customerLegalNameSnapshot },
            ...(showUnits
              ? [
                  {
                    key: 'unit',
                    header: 'Punto de venta',
                    render: (invoice: Invoice) => invoice.operatingUnitName ?? '—',
                  },
                ]
              : []),
            {
              key: 'period',
              header: 'Período',
              render: (invoice) =>
                `${formatCalendarDate(invoice.periodStart)} – ${formatCalendarDate(invoice.periodEnd)}`,
            },
            {
              key: 'total',
              header: 'Total',
              render: (invoice) => formatPriceCents(invoice.totalAmountCents),
            },
            {
              key: 'saldo',
              header: 'Saldo',
              render: renderSaldo,
            },
            {
              key: 'status',
              header: 'Estado',
              render: (invoice) => <StatusBadge domain="invoice" status={invoice.status} />,
            },
          ]}
          renderCardHeader={(invoice) => (
            <Link
              to="/invoices/$id"
              params={{ id: invoice.id }}
              className="flex items-center justify-between gap-2"
            >
              <span>
                {formatInvoiceNumber(invoice.pointOfSale, invoice.invoiceType, invoice.number)}
              </span>
              <StatusBadge domain="invoice" status={invoice.status} />
            </Link>
          )}
          renderCardBody={(invoice) => (
            <Link
              to="/invoices/$id"
              params={{ id: invoice.id }}
              className="col-span-2 grid grid-cols-2 gap-2"
            >
              <span>{invoice.customerLegalNameSnapshot}</span>
              {showUnits ? <span>{invoice.operatingUnitName ?? '—'}</span> : null}
              <span>{formatPriceCents(invoice.totalAmountCents)}</span>
              <span>Saldo: {renderSaldo(invoice)}</span>
              <span className="col-span-2">
                {formatCalendarDate(invoice.periodStart)} – {formatCalendarDate(invoice.periodEnd)}
              </span>
            </Link>
          )}
        />
      ) : null}

      {generateModalOpen ? (
        <Modal
          open
          onClose={() => setGenerateModalOpen(false)}
          title="Generar borradores"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {draftRangeError ? (
              <Alert tone="danger">La fecha "desde" no puede ser posterior a la fecha "hasta".</Alert>
            ) : null}
            {generateMutation.isError ? (
              <Alert tone="danger">
                {mapInvoiceError(generateMutation.error)}
              </Alert>
            ) : null}
            {generateMutation.data && generateMutation.data.skippedOperatingUnits.length > 0 ? (
              <Alert tone="warning">
                <p>
                  {generateMutation.data.invoices.length === 0
                    ? 'No se generó ningún borrador.'
                    : `Se generaron ${generateMutation.data.invoices.length} borradores.`}{' '}
                  Se omitieron remitos de estos puntos de venta:
                </p>
                <ul className="list-disc pl-5">
                  {generateMutation.data.skippedOperatingUnits.map((unit) => (
                    <li key={`${unit.operatingUnitId}|${unit.reason}`}>
                      {skippedOperatingUnitMessage(unit)}
                    </li>
                  ))}
                </ul>
                <Link to="/settings/points-of-sale" className="font-medium underline">
                  Ir a Configuración
                </Link>
              </Alert>
            ) : null}
            <Field
              label="Período desde"
              name="draftPeriodStart"
              type="date"
              required
              value={draftPeriodStart}
              onChange={(event) => setDraftPeriodStart(event.target.value)}
            />
            <Field
              label="Período hasta"
              name="draftPeriodEnd"
              type="date"
              required
              value={draftPeriodEnd}
              onChange={(event) => setDraftPeriodEnd(event.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                loading={generateMutation.isPending}
                onClick={confirmGenerate}
              >
                Confirmar
              </Button>
              <Button variant="secondary" onClick={() => setGenerateModalOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {batchModalOpen ? (
        <Modal
          open
          onClose={closeBatchModal}
          title="Emitir facturas seleccionadas"
          className="max-w-lg"
        >
          <div className="flex flex-col gap-4">
            {batchMutation.isError ? (
              <Alert tone="danger">{mapInvoiceError(batchMutation.error)}</Alert>
            ) : null}

            {batchMutation.data === undefined ? (
              <>
                <p className="text-sm text-slate-600">
                  Vas a solicitar el CAE ante ARCA para {selectedIds.size}{' '}
                  {selectedIds.size === 1 ? 'factura' : 'facturas'}. Esta acción no se puede
                  deshacer.
                </p>
                <ul className="list-disc pl-5 text-sm text-slate-700">
                  {batchInvoices.map((invoice) => (
                    <li key={invoice.id}>
                      {formatInvoiceNumber(invoice.pointOfSale, invoice.invoiceType, invoice.number)} ·{' '}
                      {invoice.customerLegalNameSnapshot}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-3">
                  <Button loading={batchMutation.isPending} onClick={() => batchMutation.mutate()}>
                    Confirmar emisión
                  </Button>
                  <Button variant="secondary" onClick={closeBatchModal}>
                    Cancelar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Table>
                  <caption className="sr-only">Resultado de la emisión por lote</caption>
                  <thead>
                    <tr>
                      <TableHeader>Factura</TableHeader>
                      <TableHeader>Resultado</TableHeader>
                    </tr>
                  </thead>
                  <TableBody>
                    {batchMutation.data.map((result) => (
                      <TableRow key={result.invoiceId}>
                        <TableCell>
                          <Link
                            to="/invoices/$id"
                            params={{ id: result.invoiceId }}
                            className="underline"
                          >
                            {batchLabel(result)}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {result.outcome === 'SKIPPED' ? (
                              <span className="text-sm font-medium text-slate-700">Omitida</span>
                            ) : (
                              <StatusBadge {...BATCH_OUTCOME_BADGE[result.outcome]} />
                            )}
                            {result.reason !== undefined ? (
                              <span className="text-xs text-slate-500">{result.reason}</span>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div>
                  <Button variant="secondary" onClick={closeBatchModal}>
                    Cerrar
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
